/**
 * The site's public face in one place: the brand strings (`src/brand.ts`), the site URL, the
 * public pages' titles and descriptions, and what crawlers may read.
 *
 * Plain data and string helpers only, no browser or React: `vite.config.ts` and
 * `scripts/prerender.mjs` read it at build time, the app at run time.
 */

// Relative, not `@/`: vite.config.ts and scripts/prerender.mjs load this outside the alias.
import { BRAND } from '../brand';

export { BRAND };

/**
 * Where the site lives: the only place the domain is written. `VITE_SITE_URL` at build time
 * overrides it; canonical links, the sitemap, robots.txt, llms.txt and the structured data all
 * take it from {@link resolveSiteUrl}.
 */
export const DEFAULT_SITE_URL = 'https://quantphi.app';

/** `VITE_SITE_URL` without a trailing slash, or the default when unset or blank. */
export function resolveSiteUrl(value: string | undefined): string {
  const url = (value ?? '').trim().replace(/\/+$/, '');
  return url || DEFAULT_SITE_URL;
}

export type SeoLocale = 'en' | 'tr';

/**
 * The language of the root addresses (`/`, `/login`): what a crawler and a new visitor read
 * first, and the language of app.html. A visitor in another language gets the page rendered in
 * theirs (see `seo/prerender.ts`).
 */
export const PRERENDER_LOCALE: SeoLocale = 'en';

/**
 * The languages every public page is prerendered in, each at its own address: English at the
 * root (`/login`), the others under their code (`/tr/login`). hreflang and the sitemap pair them,
 * and `src/i18n` opens a coded address in its language whatever was saved or the browser says.
 */
export const PAGE_LOCALES = ['en', 'tr'] as const satisfies readonly SeoLocale[];

export const OG_LOCALES: Record<SeoLocale, string> = { en: 'en_US', tr: 'tr_TR' };

export interface OgImage {
  path: string;
  width: number;
  height: number;
  alt: string;
}

/** The link-preview picture, served from `public/`. */
export const OG_IMAGE = {
  path: '/og-quantphi-aether-v1.png',
  width: 1200,
  height: 630,
  alt: `${BRAND.platform} — See beyond the market. Build your strategy. Stay in control.`,
} as const satisfies OgImage;

/**
 * The results page's own preview (`scripts/generate-results-og-image.mjs`): the same Aether
 * card, with no figure on it — the shelf's numbers change every pass, a picture would go stale.
 */
export const RESULTS_OG_IMAGE = {
  path: '/og-quantphi-results-v2.png',
  width: 1200,
  height: 630,
  alt: `${BRAND.platform} — Live bot results: real bots and forward-tested backtests, in percent.`,
} as const satisfies OgImage;

export interface PublicPage {
  path: string;
  title: Record<SeoLocale, string>;
  description: Record<SeoLocale, string>;
  /** The link preview's picture; `OG_IMAGE` when the page has none of its own. */
  image?: OgImage;
}

/** Every page a visitor can open without signing in. The sitemap lists them all. */
export const PUBLIC_PAGES = {
  home: {
    path: '/',
    title: {
      en: `${BRAND.platform} — Crypto Trading Bots & Autopilot`,
      tr: `${BRAND.platform} — Kripto Alım-Satım Botları ve Otopilot`,
    },
    description: {
      en: 'Run DCA, grid, rebalance and Hedge Stop bots on your own exchange account. Market scanner, autopilot, backtests, hedge rescue and Telegram alerts, with you in control.',
      tr: 'Kendi borsa hesabında DCA, grid, rebalans ve Hedge Stop botları çalıştır. Piyasa tarayıcısı, otopilot, backtest, hedge kurtarma ve Telegram bildirimleri; kontrol sende kalır.',
    },
  },
  login: {
    path: '/login',
    title: {
      en: `Sign in — ${BRAND.platform}`,
      tr: `Giriş yap — ${BRAND.platform}`,
    },
    description: {
      en: `Sign in to ${BRAND.platform} to manage your trading bots, open deals and autopilot.`,
      tr: `Botlarınızı, açık işlemlerinizi ve otopilotu yönetmek için ${BRAND.platform} hesabınıza giriş yapın.`,
    },
  },
  register: {
    path: '/register',
    title: {
      en: `Create your account — ${BRAND.platform}`,
      tr: `Hesap oluştur — ${BRAND.platform}`,
    },
    description: {
      en: `Create your ${BRAND.platform} account, connect your exchange with an API key and set up your first DCA bot in minutes.`,
      tr: `${BRAND.platform} hesabınızı oluşturun, borsanızı API anahtarıyla bağlayın ve dakikalar içinde ilk DCA botunuzu kurun.`,
    },
  },
  results: {
    path: '/results',
    title: {
      en: `Live bot results — ${BRAND.platform}`,
      tr: `Canlı bot sonuçları — ${BRAND.platform}`,
    },
    description: {
      en: `Live results of DCA bots on ${BRAND.platform}: users' running bots, shown anonymously, and backtests checked on days they never saw. Returns in percent; past results do not guarantee future returns.`,
      tr: `${BRAND.platform}’deki DCA botlarının canlı sonuçları: kullanıcıların çalışan botları (anonim) ve hiç görmediği günlerde de sınanan backtestler. Getiriler yüzde olarak; geçmiş sonuçlar gelecekteki getirinin garantisi değildir.`,
    },
    image: RESULTS_OG_IMAGE,
  },
  // docs/LEGAL.md — the texts themselves live in features/legal/content.
  terms: {
    path: '/terms',
    title: {
      en: `Terms of Use — ${BRAND.platform}`,
      tr: `Kullanım Şartları — ${BRAND.platform}`,
    },
    description: {
      en: `The rules for using ${BRAND.platform}: a software tool that trades on your own exchange account with your API key. Not investment advice; your funds stay on your exchange.`,
      tr: `${BRAND.platform} kullanım şartları: kendi borsa hesabınızda, kendi API anahtarınızla işlem yapan bir yazılım aracı. Yatırım tavsiyesi değildir; varlıklarınız borsanızda kalır.`,
    },
  },
  privacy: {
    path: '/privacy',
    title: {
      en: `Privacy and KVKK Notice — ${BRAND.platform}`,
      tr: `Gizlilik ve KVKK Aydınlatma Metni — ${BRAND.platform}`,
    },
    description: {
      en: `Which personal data ${BRAND.platform} processes, why, where it is stored and your rights under the Turkish data protection law (KVKK).`,
      tr: `${BRAND.platform}’nin hangi kişisel verileri hangi amaçla işlediği, nerede sakladığı ve KVKK kapsamındaki haklarınız.`,
    },
  },
  risk: {
    path: '/risk',
    title: {
      en: `Risk Notice — ${BRAND.platform}`,
      tr: `Risk Bildirimi — ${BRAND.platform}`,
    },
    description: {
      en: 'Crypto trading and automated bots carry serious risks: leverage, liquidation, strategy risks, and why past results and backtests guarantee nothing.',
      tr: 'Kripto alım-satımı ve otomatik botlar ciddi riskler taşır: kaldıraç, tasfiye, stratejilerin riskleri ve geçmiş sonuçlarla backtestlerin neden garanti olmadığı.',
    },
  },
} as const satisfies Record<string, PublicPage>;

export type PublicPageKey = keyof typeof PUBLIC_PAGES;

/** The preview picture of a public page: its own, else the site's. */
export function pageImage(key: PublicPageKey): OgImage {
  const page: PublicPage = PUBLIC_PAGES[key];
  return page.image ?? OG_IMAGE;
}

/** Title and description of the signed-in app's pages, which crawlers are kept out of. */
export const APP_HEAD = {
  title: BRAND.platform,
  description: PUBLIC_PAGES.home.description,
} as const;

/** The landing page's FAQ entries (`nexus.landing.q{n}` / `a{n}`), also published as FAQPage. */
export const LANDING_FAQ = [1, 2, 3, 4, 5, 6, 7, 8, 9, 10] as const;

/**
 * Everything behind sign-in (router.tsx) plus the API: nothing there for a crawler. Prefixes, as
 * robots.txt reads them; `__tests__/seo.test.ts` checks every route in `ROUTES` is covered.
 */
export const PRIVATE_PATHS = [
  '/api/',
  '/dashboard',
  '/bots',
  '/deals',
  '/positions',
  '/insights',
  '/autopilot',
  '/showcase',
  '/volatility',
  '/volume',
  '/lab',
  '/copy-trade',
  '/backtest',
  '/web-sources',
  '/scanner',
  '/billing',
  '/settings',
  '/changelog',
  '/admin',
  '/exchange-accounts',
  '/exchanges',
] as const;

/**
 * AI crawlers named in robots.txt so the welcome is explicit. They share the `*` group's rules,
 * so a crawler that finds its own name reads the same allow and disallow lines.
 */
export const AI_CRAWLERS = [
  'GPTBot',
  'OAI-SearchBot',
  'ChatGPT-User',
  'ClaudeBot',
  'Claude-SearchBot',
  'Claude-User',
  'PerplexityBot',
  'Perplexity-User',
  'Google-Extended',
  'Applebot-Extended',
  'CCBot',
] as const;

/**
 * The language of a page's title and description: Turkish for Turkish, English for every other
 * language (only these two are written; the page text itself follows the visitor's language).
 */
export function seoLocale(value: string | null | undefined): SeoLocale {
  return value?.toLowerCase().startsWith('tr') ? 'tr' : 'en';
}

/** The absolute URL of a path on the site. */
export function absoluteUrl(siteUrl: string, path: string): string {
  return path === '/' ? `${siteUrl}/` : `${siteUrl}${path}`;
}

const PUBLIC_PATHS = new Set<string>(Object.values(PUBLIC_PAGES).map((page) => page.path));

/** True for a public page's root address (`/login`), the ones with a copy per language. */
export function isPublicPath(path: string): boolean {
  return PUBLIC_PATHS.has(path);
}

/** A public page's address in a language: `/login` → `/tr/login`, `/` → `/tr/`. */
export function localePath(path: string, locale: SeoLocale): string {
  return locale === PRERENDER_LOCALE ? path : `/${locale}${path}`;
}

/** The language an address is coded with (`/tr`, `/tr/`, `/tr/login`), or null for a root one. */
export function pathLocale(pathname: string): SeoLocale | null {
  return (
    PAGE_LOCALES.find(
      (locale) =>
        locale !== PRERENDER_LOCALE &&
        (pathname === `/${locale}` || pathname.startsWith(`/${locale}/`)),
    ) ?? null
  );
}

/** An address without its language code: `/tr/login` → `/login`, `/tr` → `/`. */
export function stripLocale(pathname: string): string {
  const locale = pathLocale(pathname);
  return locale ? pathname.slice(locale.length + 1) || '/' : pathname;
}

/** A page's address in every language, plus `x-default` (the root one): hreflang's list. */
export function pageAlternates(
  siteUrl: string,
  path: string,
): { hreflang: string; href: string }[] {
  return [
    ...PAGE_LOCALES.map((locale) => ({
      hreflang: locale,
      href: absoluteUrl(siteUrl, localePath(path, locale)),
    })),
    { hreflang: 'x-default', href: absoluteUrl(siteUrl, path) },
  ];
}

/**
 * The file in dist/ an address is written to, the way nginx.conf finds it: `/x` is `x.html`
 * (`$uri.html`), `/x/` is `x/index.html` (`$uri/` and the index).
 */
export function pageFile(path: string): string {
  return path.endsWith('/') ? `${path.slice(1)}index.html` : `${path.slice(1)}.html`;
}
